import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Lock, User, Eye, EyeOff } from 'lucide-react';
import { BrandLogo } from '../BrandLogo';

const DriverLoginPage = () => {
  const navigate = useNavigate();
  const { loginDriver, formatApiError } = useAuth();
  const [driverId, setDriverId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!driverId.trim() || !password.trim()) {
      toast({
        title: 'Missing Credentials',
        description: 'Please enter both Driver ID and Password',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const driver = await loginDriver(driverId.trim(), password);
      toast({ title: 'Login Successful!', description: `Welcome back, ${driver.name}!` });
      navigate('/driver/dashboard');
    } catch (err) {
      toast({
        title: 'Login Failed',
        description: formatApiError(err),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card to-muted flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Button variant="ghost" onClick={() => navigate('/')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <BrandLogo variant="compact" className="h-14 w-auto mx-auto mb-4" />
          <p className="text-muted-foreground">Driver Login</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Driver Login</CardTitle>
            <CardDescription>Login with your Driver ID and Password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="driverId">Driver ID</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 w-5 h-5 text-muted-foreground/70" />
                  <Input
                    id="driverId"
                    type="text"
                    placeholder="Enter your Driver ID (e.g., DRV123456)"
                    value={driverId}
                    onChange={(e) => setDriverId(e.target.value.toUpperCase())}
                    className="pl-10 h-12"
                    data-testid="driver-login-id-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-5 h-5 text-muted-foreground/70" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12"
                    data-testid="driver-login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-3 text-muted-foreground/70 hover:text-foreground"
                    data-testid="toggle-password-visibility-btn"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary-dark h-12"
                data-testid="driver-login-submit-btn"
              >
                {submitting ? 'Logging in…' : 'Login'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground mb-2">Don't have a Driver ID?</p>
              <Button
                variant="link"
                onClick={() => navigate('/driver/register')}
                className="text-primary"
              >
                Register as a Driver
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DriverLoginPage;
